<script lang="ts">
	import Tooltip from '../../Tooltip.svelte';
	import type { InputRangeProps } from '../types';
	import Input from '../Input.svelte';
	import { attrValue } from '$lib/utils/html';
	import { colors } from '$lib/theme/colors';

	export let props: InputRangeProps;
	export let inputCenter = false;

	let inputWidth;
	let thumbWidth = props?.thumbWidth ?? 20;
	let thumbHalfWidth = thumbWidth / 2;
	let trackHeight = props?.trackHeight ?? 2;
	let thumbColor = props?.thumbColor ?? 'primary';
	let trackColor = props?.trackColor ?? 'secondary';
	let leftDescription = props?.leftDescription;
	let rightDescription = props?.rightDescription;

	colors;

	if (props) {
		props.min = props.min ?? 0;
		props.max = props.max ?? 10000;
		props.step = props.step ?? 1;
		props.value = props.value ?? props.min;
	}

	const handleChange = () => {
		if (!props.touched) {
			props.touched = true;
		}
		if (!props.dirty) {
			props.dirty = true;
		}
	};

	// https://stackoverflow.com/questions/48880523/how-to-precisely-find-thumb-position-of-input-type-range
	$: tooltipPosition =
		inputWidth &&
		((props.value - props.min) / (props.max - props.min)) *
			(inputWidth - thumbHalfWidth - thumbHalfWidth) +
			thumbHalfWidth;
</script>

<Input bind:props>
	<div
		class={attrValue([
			'flex flex-col items-center relative',
			inputCenter ? 'justify-center w-full md:w-2/3 xl:w-2/3 mt-6' : ''
		])}
		bind:clientWidth={inputWidth}
	>
		<input
			id={props.id}
			type="range"
			step={props.step}
			name={props.name}
			min={props.min}
			max={props.max}
			bind:value={props.value}
			class="appearance-none w-full bg-{trackColor} rounded h-{trackHeight} rounded outline-none slider-thumb"
			style="--thumbWidth: {thumbWidth}px; --thumbColor: {colors[
				thumbColor
			]}; --thumbBorderColor: {colors[trackColor]}"
			on:change|once={() => handleChange()}
			disabled={props.disabled}
		/>
		{#if props && props.leftDescription && props.rightDescription}
			<div class="flex justify-between mt-3 w-full">
				<p class="text-sm">{props.leftDescription}</p>
				<p class="text-sm">{props.rightDescription}</p>
			</div>
		{/if}
		{#if props && !props.hideTooltip}
			<div
				class="absolute bottom-full transform -translate-x-1/2"
				style={`left:${tooltipPosition}px`}
			>
				{#if props.value}
					<Tooltip placement="top" title={props.value.toString()} fixedTooltipArrowPosition />
				{/if}
			</div>
		{/if}
	</div>
</Input>

<style lang="postcss">
	.slider-thumb::-webkit-slider-thumb {
		@apply appearance-none rounded-full cursor-pointer;
		background-color: var(--thumbColor);
		width: var(--thumbWidth);
		height: var(--thumbWidth);
		border: 1px solid var(--thumbBorderColor);
	}
	.slider-thumb::-moz-range-thumb {
		@apply appearance-none rounded-full bg-primary cursor-pointer;
		background-color: var(--thumbColor);
		width: var(--thumbWidth);
		height: var(--thumbWidth);
		border: 1px solid var(--thumbBorderColor);
	}
	.slider-thumb::-ms-thumb {
		@apply appearance-none rounded-full bg-primary cursor-pointer;
		background-color: var(--thumbColor);
		width: var(--thumbWidth);
		height: var(--thumbWidth);
		border: 1px solid var(--thumbBorderColor);
	}

	.slider-thumb::-webkit-slider-thumb:hover,
	.slider-thumb::-moz-range-thumb:hover,
	.slider-thumb::-ms-thumb:hover {
		@apply opacity-30;
	}
</style>
